/**
 * Creator Referral Hub — personalized link generation + parsing.
 * Pure frontend: encodes intent (creator, influencer, nation, challenge,
 * sponsor) into URL params consumed by takeover, showcase and onboarding.
 */
import { useEffect, useState } from "react";

export type CreatorLinkKind = "launch" | "showcase" | "challenge" | "takeover";

export interface CreatorLinkInput {
  kind: CreatorLinkKind;
  creator?: string;
  influencer?: string;
  nation?: string;
  challenge?: string;
  sponsor?: string;
  ref?: string;
}

const ROUTE: Record<CreatorLinkKind, string> = {
  launch: "/welcome",
  showcase: "/showcase",
  challenge: "/ambassadors",
  takeover: "/showcase",
};

export function buildCreatorLink(input: CreatorLinkInput): string {
  const base = typeof window !== "undefined" ? window.location.origin : "https://versesport.com";
  const path = ROUTE[input.kind];
  const sp = new URLSearchParams();
  if (input.creator) sp.set("creator", input.creator);
  if (input.influencer) sp.set("influencer", input.influencer);
  if (input.nation) sp.set("nation", input.nation);
  if (input.challenge) sp.set("challenge", input.challenge);
  if (input.sponsor) sp.set("sponsor", input.sponsor);
  if (input.ref) sp.set("ref", input.ref);
  const q = sp.toString();
  return `${base}${path}${q ? `?${q}` : ""}`;
}

export interface CreatorContext {
  creator: string | null;
  influencer: string | null;
  nation: string | null;
  challenge: string | null;
  sponsor: string | null;
  ref: string | null;
  active: boolean;
}

const EMPTY: CreatorContext = {
  creator: null, influencer: null, nation: null,
  challenge: null, sponsor: null, ref: null, active: false,
};

export function readCreatorContext(): CreatorContext {
  if (typeof window === "undefined") return EMPTY;
  const sp = new URLSearchParams(window.location.search);
  const get = (k: string) => {
    const v = sp.get(k);
    return v && v.trim().length ? v.trim() : null;
  };
  const ctx: CreatorContext = {
    creator: get("creator"),
    influencer: get("influencer"),
    nation: get("nation"),
    challenge: get("challenge"),
    sponsor: get("sponsor"),
    ref: get("ref"),
    active: false,
  };
  ctx.active = !!(ctx.creator || ctx.influencer || ctx.nation || ctx.challenge || ctx.ref);
  return ctx;
}

export function useCreatorContext(): CreatorContext {
  const [ctx, setCtx] = useState<CreatorContext>(EMPTY);
  useEffect(() => { setCtx(readCreatorContext()); }, []);
  return ctx;
}

/**
 * Persist a creator/referral source on first arrival so later page changes
 * still attribute the visit to the correct creator/nation.
 */
const KEY = "vs:ambassadors:source";

export interface PersistedSource extends CreatorContext { firstSeen: number }

export function usePersistedCreatorSource(): PersistedSource | null {
  const ctx = useCreatorContext();
  const [stored, setStored] = useState<PersistedSource | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    let cur: PersistedSource | null = null;
    try {
      const raw = window.localStorage.getItem(KEY);
      cur = raw ? (JSON.parse(raw) as PersistedSource) : null;
    } catch { /* noop */ }
    if (!cur && ctx.active) {
      cur = { ...ctx, firstSeen: Date.now() };
      try { window.localStorage.setItem(KEY, JSON.stringify(cur)); } catch { /* noop */ }
    }
    setStored(cur);
  }, [ctx.active, ctx.creator, ctx.influencer, ctx.nation, ctx.challenge, ctx.ref]);
  return stored;
}
